import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Save, 
  Loader2, 
  User,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Plus,
  Dumbbell,
  Clipboard,
  Trash2
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';

const TPI_CATEGORIES = [
  {
    id: 'posture_pelvic',
    name: 'Posture & Pelvic Control',
    color: '#7C3AED',
    description: 'Foundation tests for initial golf posture and stability',
    tests: [
      { id: 'pelvic_tilt', name: 'Pelvic Tilt', description: 'Can the golfer tilt their pelvis anteriorly and posteriorly while maintaining posture?' },
      { id: 'pelvic_rotation', name: 'Pelvic Rotation', description: 'Can the golfer rotate pelvis independently from upper body?' },
      { id: 'bridge_w_leg_ext', name: 'Bridge with Leg Extension', description: 'Can the golfer hold a bridge while extending one leg without dropping the hips?' },
      { id: 'lower_quarter_rotation', name: 'Lower Quarter Rotation', description: 'Tests hip mobility in golf posture position' }
    ]
  },
  {
    id: 'rotation',
    name: 'Rotation & Mobility',
    color: '#2563EB',
    description: 'Critical for swing plane and rotational power',
    tests: [
      { id: 'seated_trunk_rotation', name: 'Seated Trunk Rotation', description: 'Can the golfer rotate torso 45+ degrees in each direction?' },
      { id: 'torso_rotation', name: 'Torso Rotation (Standing)', description: 'Standing torso rotation test - should achieve 60+ degrees' },
      { id: 'hip_rotation_internal', name: 'Hip Internal Rotation', description: 'Should achieve 40-60 degrees of internal hip rotation' },
      { id: 'hip_rotation_external', name: 'Hip External Rotation', description: 'Should achieve 40-60 degrees of external hip rotation' },
      { id: '90_90_shoulder', name: '90/90 Shoulder Test', description: 'Tests shoulder external rotation in abducted position' },
      { id: 'lat_length', name: 'Lat Length Test', description: 'Tests latissimus dorsi flexibility affecting overhead movements' }
    ]
  },
  {
    id: 'flexibility',
    name: 'Flexibility',
    color: '#059669',
    description: 'Overall flexibility affecting swing mechanics',
    tests: [
      { id: 'toe_touch', name: 'Toe Touch', description: 'Can the golfer touch toes with straight legs? Tests hamstring and lower back flexibility' },
      { id: 'overhead_deep_squat', name: 'Overhead Deep Squat', description: 'Full body mobility test with arms overhead' },
      { id: 'cervical_rotation', name: 'Cervical Rotation', description: 'Neck rotation should be 70+ degrees each way' }
    ]
  },
  {
    id: 'stability_balance',
    name: 'Stability & Balance',
    color: '#DC2626',
    description: 'Core and lower-body control throughout the swing',
    tests: [
      { id: 'single_leg_balance', name: 'Single Leg Balance', description: 'Can hold balance for 15+ seconds with eyes closed?' },
      { id: 'single_leg_squat', name: 'Single Leg Squat', description: 'Tests hip stability and strength during single leg movements' }
    ]
  },
  {
    id: 'wrist_forearm',
    name: 'Wrists & Forearms',
    color: '#F59E0B',
    description: 'Grip and impact control',
    tests: [
      { id: 'wrist_hinge', name: 'Wrist Hinge Test', description: 'Tests wrist mobility for proper club release' },
      { id: 'wrist_flexion_extension', name: 'Wrist Flexion/Extension', description: 'Full range wrist mobility assessment' },
      { id: 'forearm_rotation', name: 'Forearm Rotation', description: 'Pronation/supination for club face control' }
    ]
  }
];

const EXERCISE_SUGGESTIONS = {
  posture_pelvic: [
    'Cat-Cow stretches', 'Pelvic tilts on stability ball', 'Dead bugs', 'Hip bridges',
    'Bird dogs', 'Glute activation exercises', 'Core bracing drills'
  ],
  rotation: [
    'Open book stretch', 'Thread the needle', 'Hip 90/90 stretch', 'Torso rotation with club',
    'Seated rotation stretches', 'Hip flexor stretches', 'Thoracic spine mobility work'
  ],
  flexibility: [
    'Standing hamstring stretch', 'Downward dog', "World's greatest stretch", 'Hip circles',
    'Neck stretches', 'Lat stretch on wall', 'Goblet squat holds'
  ],
  stability_balance: [
    'Single leg deadlifts', 'BOSU ball exercises', 'Balance board work', 'Core stabilization',
    'Plank variations', 'Single leg stands', 'Eyes-closed balance drills'
  ],
  wrist_forearm: [
    'Wrist circles', 'Forearm stretches', 'Rice bucket exercises', 'Wrist curls',
    'Reverse wrist curls', 'Grip strengthening', 'Wrist rotations with light weight'
  ]
};

function buildEmptyCategories() {
  const categories = {};
  TPI_CATEGORIES.forEach(cat => {
    categories[cat.id] = { tests: {}, notes: '', priority: false };
    cat.tests.forEach(test => {
      categories[cat.id].tests[test.id] = {
        result: null,
        notes: '',
        leftSide: null,
        rightSide: null
      };
    });
  });
  return categories;
}

const TPIScreeningForm = () => {
  const { clientId, screeningId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [client, setClient] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState(['posture_pelvic']);
  const isEditing = !!screeningId;

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    overall_notes: '',
    categories: buildEmptyCategories(),
    prescriptions: []
  });

  useEffect(() => {
    const load = async () => {
      try {
        const clientRes = await API.get(`/coach/clients/${clientId}`);
        setClient(clientRes.data);

        if (screeningId) {
          const screeningRes = await API.get(`/coach/tpi-screening/${screeningId}`);
          const s = screeningRes.data;
          const merged = buildEmptyCategories();
          Object.keys(s.categories || {}).forEach(catId => {
            if (merged[catId]) {
              merged[catId].notes = s.categories[catId].notes || '';
              merged[catId].priority = s.categories[catId].priority || false;
              Object.keys(s.categories[catId].tests || {}).forEach(testId => {
                if (merged[catId].tests[testId]) {
                  merged[catId].tests[testId] = {
                    ...merged[catId].tests[testId],
                    ...s.categories[catId].tests[testId]
                  };
                }
              });
            }
          });
          setFormData({
            date: s.date || new Date().toISOString().split('T')[0],
            overall_notes: s.overall_notes || '',
            categories: merged,
            prescriptions: s.prescriptions || []
          });
          setExpandedCategories(Object.keys(merged));
        }
      } catch (error) {
        toast.error('Failed to load data');
        navigate(`/coach/clients/${clientId}`);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clientId, screeningId]);

  const toggleCategory = (catId) => {
    setExpandedCategories(prev =>
      prev.includes(catId) ? prev.filter(id => id !== catId) : [...prev, catId]
    );
  };

  const updateTestResult = (catId, testId, field, value) => {
    setFormData(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [catId]: {
          ...prev.categories[catId],
          tests: {
            ...prev.categories[catId].tests,
            [testId]: {
              ...prev.categories[catId].tests[testId],
              [field]: value
            }
          }
        }
      }
    }));
  };

  const updateCategoryNotes = (catId, notes) => {
    setFormData(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [catId]: { ...prev.categories[catId], notes }
      }
    }));
  };

  const toggleCategoryPriority = (catId) => {
    setFormData(prev => ({
      ...prev,
      categories: {
        ...prev.categories,
        [catId]: { ...prev.categories[catId], priority: !prev.categories[catId]?.priority }
      }
    }));
  };

  const addPrescription = (exercise) => {
    if (!formData.prescriptions.includes(exercise)) {
      setFormData(prev => ({ ...prev, prescriptions: [...prev.prescriptions, exercise] }));
    }
  };

  const removePrescription = (exercise) => {
    setFormData(prev => ({ ...prev, prescriptions: prev.prescriptions.filter(e => e !== exercise) }));
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEditing) {
        await API.put(`/coach/tpi-screening/${screeningId}`, formData);
        toast.success('TPI Screening updated');
      } else {
        await API.post(`/coach/tpi-screening/${clientId}`, formData);
        toast.success('TPI Screening saved');
      }
      navigate(`/coach/clients/${clientId}`);
    } catch (error) {
      toast.error('Failed to save screening');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this screening? This cannot be undone.')) return;
    setDeleting(true);
    try {
      await API.delete(`/coach/tpi-screening/${screeningId}`);
      toast.success('Screening deleted');
      navigate(`/coach/clients/${clientId}`);
    } catch (error) {
      toast.error('Failed to delete screening');
    } finally {
      setDeleting(false);
    }
  };

  const stats = (() => {
    let totalTests = 0, passed = 0, failed = 0, limited = 0;
    Object.values(formData.categories).forEach(cat => {
      Object.values(cat.tests).forEach(test => {
        if (test.result) {
          totalTests++;
          if (test.result === 'pass') passed++;
          else if (test.result === 'fail') failed++;
          else if (test.result === 'limited') limited++;
        }
      });
    });
    return { totalTests, passed, failed, limited };
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#2C5530]" />
      </div>
    );
  }

  return (
    <div data-testid="tpi-screening-form" className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/coach/clients/${clientId}`)}
          className="p-2 hover:bg-[#F2F0E9] rounded-lg transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5 text-[#57534E]" />
        </button>
        <div className="flex-1">
          <h1 className="font-['Playfair_Display'] text-2xl text-[#1A1A1A]">
            {isEditing ? 'Edit TPI Screen' : 'New TPI Physical Screen'}
          </h1>
          <p className="text-sm text-[#57534E]">
            Client: <span className="font-medium">{client?.full_name || client?.email}</span>
          </p>
        </div>
        <div className="flex items-center gap-3">
          <input
            type="date"
            value={formData.date}
            onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
            className="border border-[#E7E5E4] px-3 py-2 focus:border-[#2C5530] focus:outline-none"
            data-testid="screening-date"
          />
          {isEditing && (
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
              data-testid="delete-screening-btn"
              title="Delete screening"
            >
              <Trash2 className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-[#E7E5E4] p-4 text-center">
          <p className="text-3xl font-bold text-[#1A1A1A]" data-testid="stat-total">{stats.totalTests}</p>
          <p className="text-xs text-[#737373] uppercase">Tests Completed</p>
        </div>
        <div className="bg-green-50 border border-green-200 p-4 text-center">
          <p className="text-3xl font-bold text-green-600" data-testid="stat-pass">{stats.passed}</p>
          <p className="text-xs text-green-700 uppercase">Pass</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 p-4 text-center">
          <p className="text-3xl font-bold text-yellow-600" data-testid="stat-limited">{stats.limited}</p>
          <p className="text-xs text-yellow-700 uppercase">Limited</p>
        </div>
        <div className="bg-red-50 border border-red-200 p-4 text-center">
          <p className="text-3xl font-bold text-red-600" data-testid="stat-fail">{stats.failed}</p>
          <p className="text-xs text-red-700 uppercase">Fail</p>
        </div>
      </div>

      {/* TPI Info Banner */}
      <div className="bg-[#2C5530] text-white p-6 mb-6">
        <h2 className="text-lg font-semibold mb-2">Body-Swing Connection</h2>
        <p className="text-white/80 text-sm">
          The TPI Physical Screen identifies how physical limitations affect swing mechanics.
          There is no "one way" to swing - only the most efficient way for each golfer's body.
        </p>
      </div>

      {/* Categories */}
      <div className="space-y-4 mb-8">
        {TPI_CATEGORIES.map((category) => {
          const isExpanded = expandedCategories.includes(category.id);
          const catData = formData.categories[category.id];
          const isPriority = catData?.priority;
          const catTests = Object.values(catData?.tests || {});
          const catFailed = catTests.filter(t => t.result === 'fail' || t.result === 'limited').length;

          return (
            <div
              key={category.id}
              className={`bg-white border-2 overflow-hidden ${isPriority ? 'border-red-400' : 'border-[#E7E5E4]'}`}
              data-testid={`tpi-category-${category.id}`}
            >
              <div
                className="p-4 cursor-pointer flex items-center justify-between"
                style={{ backgroundColor: `${category.color}10` }}
                onClick={() => toggleCategory(category.id)}
              >
                <div className="flex items-center gap-3">
                  <div className="w-4 h-4 rounded-full" style={{ backgroundColor: category.color }} />
                  <div>
                    <h3 className="font-semibold text-[#1A1A1A]">{category.name}</h3>
                    <p className="text-xs text-[#737373]">{category.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  {catFailed > 0 && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">
                      {catFailed} issue{catFailed > 1 ? 's' : ''}
                    </span>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); toggleCategoryPriority(category.id); }}
                    className={`text-xs px-2 py-1 rounded ${isPriority ? 'bg-red-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                    data-testid={`priority-${category.id}`}
                  >
                    {isPriority ? 'PRIORITY' : 'Priority'}
                  </button>
                  {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                </div>
              </div>

              {isExpanded && (
                <div className="p-4 border-t border-[#E7E5E4]">
                  <div className="space-y-4">
                    {category.tests.map((test) => {
                      const testData = catData?.tests?.[test.id] || {};
                      return (
                        <div key={test.id} className="bg-[#F2F0E9] p-4 rounded" data-testid={`test-${test.id}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div>
                              <p className="font-medium text-[#1A1A1A]">{test.name}</p>
                              <p className="text-xs text-[#737373]">{test.description}</p>
                            </div>
                          </div>

                          <div className="flex flex-wrap gap-2 mb-3">
                            {['pass', 'limited', 'fail'].map(r => {
                              const cfg = {
                                pass: { icon: CheckCircle, label: 'Pass', active: 'bg-green-500 text-white', idle: 'bg-white border border-green-300 text-green-700 hover:bg-green-50' },
                                limited: { icon: AlertCircle, label: 'Limited', active: 'bg-yellow-500 text-white', idle: 'bg-white border border-yellow-300 text-yellow-700 hover:bg-yellow-50' },
                                fail: { icon: XCircle, label: 'Fail', active: 'bg-red-500 text-white', idle: 'bg-white border border-red-300 text-red-700 hover:bg-red-50' }
                              }[r];
                              const Icon = cfg.icon;
                              return (
                                <button
                                  key={r}
                                  onClick={() => updateTestResult(category.id, test.id, 'result', r)}
                                  className={`flex items-center gap-1 px-3 py-1.5 text-sm rounded transition-colors ${testData.result === r ? cfg.active : cfg.idle}`}
                                  data-testid={`test-${test.id}-${r}`}
                                >
                                  <Icon className="w-4 h-4" />
                                  {cfg.label}
                                </button>
                              );
                            })}

                            <div className="flex items-center gap-2 ml-4 border-l border-[#E7E5E4] pl-4">
                              <span className="text-xs text-[#737373]">L:</span>
                              <select
                                value={testData.leftSide || ''}
                                onChange={(e) => updateTestResult(category.id, test.id, 'leftSide', e.target.value || null)}
                                className="text-xs border border-[#E7E5E4] rounded px-2 py-1"
                              >
                                <option value="">-</option>
                                <option value="pass">Pass</option>
                                <option value="limited">Limited</option>
                                <option value="fail">Fail</option>
                              </select>
                              <span className="text-xs text-[#737373]">R:</span>
                              <select
                                value={testData.rightSide || ''}
                                onChange={(e) => updateTestResult(category.id, test.id, 'rightSide', e.target.value || null)}
                                className="text-xs border border-[#E7E5E4] rounded px-2 py-1"
                              >
                                <option value="">-</option>
                                <option value="pass">Pass</option>
                                <option value="limited">Limited</option>
                                <option value="fail">Fail</option>
                              </select>
                            </div>
                          </div>

                          <input
                            type="text"
                            value={testData.notes || ''}
                            onChange={(e) => updateTestResult(category.id, test.id, 'notes', e.target.value)}
                            placeholder="Notes for this test..."
                            className="w-full text-sm border border-[#E7E5E4] rounded px-3 py-2 focus:border-[#2C5530] focus:outline-none"
                            data-testid={`test-${test.id}-notes`}
                          />
                        </div>
                      );
                    })}
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#E7E5E4]">
                    <label className="block text-sm font-medium text-[#1A1A1A] mb-2">Category Notes & Observations</label>
                    <textarea
                      value={catData?.notes || ''}
                      onChange={(e) => updateCategoryNotes(category.id, e.target.value)}
                      rows={2}
                      placeholder={`Notes about ${category.name}...`}
                      className="w-full text-sm border border-[#E7E5E4] rounded px-3 py-2 focus:border-[#2C5530] focus:outline-none resize-none"
                    />
                  </div>

                  <div className="mt-4 pt-4 border-t border-[#E7E5E4]">
                    <p className="text-sm font-medium text-[#1A1A1A] mb-2 flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-[#2C5530]" />
                      Suggested Exercises
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {(EXERCISE_SUGGESTIONS[category.id] || []).map((exercise) => (
                        <button
                          key={exercise}
                          onClick={() => addPrescription(exercise)}
                          className={`text-xs px-2 py-1 rounded transition-colors ${
                            formData.prescriptions.includes(exercise)
                              ? 'bg-[#2C5530] text-white'
                              : 'bg-white border border-[#E7E5E4] text-[#57534E] hover:border-[#2C5530]'
                          }`}
                        >
                          + {exercise}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* S&C Prescription Summary */}
      <div className="bg-white border border-[#E7E5E4] p-6 mb-6">
        <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
          <Clipboard className="w-5 h-5 text-[#2C5530]" />
          S&C Prescription
        </h3>

        {formData.prescriptions.length === 0 ? (
          <p className="text-[#737373] text-sm">Click on suggested exercises above to add them to the prescription.</p>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="prescription-list">
            {formData.prescriptions.map((exercise) => (
              <span key={exercise} className="flex items-center gap-1 bg-[#2C5530] text-white px-3 py-1 text-sm rounded">
                {exercise}
                <button onClick={() => removePrescription(exercise)} className="ml-1 hover:text-red-200">x</button>
              </span>
            ))}
          </div>
        )}

        <div className="mt-4 flex gap-2">
          <input
            type="text"
            id="custom-prescription"
            placeholder="Add custom exercise..."
            className="flex-1 text-sm border border-[#E7E5E4] rounded px-3 py-2 focus:border-[#2C5530] focus:outline-none"
            data-testid="custom-prescription-input"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && e.target.value.trim()) {
                addPrescription(e.target.value.trim());
                e.target.value = '';
              }
            }}
          />
          <button
            onClick={() => {
              const input = document.getElementById('custom-prescription');
              if (input.value.trim()) {
                addPrescription(input.value.trim());
                input.value = '';
              }
            }}
            className="px-4 py-2 bg-[#F2F0E9] text-[#57534E] hover:bg-[#E7E5E4] transition-colors rounded"
            data-testid="add-prescription-btn"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Overall Notes */}
      <div className="bg-white border border-[#E7E5E4] p-6 mb-8">
        <label className="block font-semibold text-[#1A1A1A] mb-2">Overall Screening Notes & Summary</label>
        <textarea
          value={formData.overall_notes}
          onChange={(e) => setFormData(prev => ({ ...prev, overall_notes: e.target.value }))}
          rows={4}
          placeholder="Key findings, priority areas, swing implications, recommended next steps..."
          className="w-full border border-[#E7E5E4] rounded px-4 py-3 focus:border-[#2C5530] focus:outline-none resize-none"
          data-testid="overall-notes"
        />
      </div>

      {/* Actions */}
      <div className="flex justify-between items-center pb-8">
        <button
          onClick={() => navigate(`/coach/clients/${clientId}`)}
          className="text-[#57534E] hover:text-[#1A1A1A] transition-colors"
          data-testid="cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex items-center gap-2 bg-[#2C5530] text-white px-8 py-3 font-semibold uppercase tracking-wider hover:bg-[#1E3A21] transition-colors disabled:opacity-50"
          data-testid="save-screening-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-5 h-5" />
              {isEditing ? 'Update Screening' : 'Save Screening'}
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default TPIScreeningForm;
